import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, User, Calendar, Star, MessageSquare, Camera, CheckCircle } from 'lucide-react';
import { feedbackService } from '../../services/feedbackService';
import { decryptId } from '../../utils/urlHelper';
import styles from './FeedbackDetailView.module.scss';

const FeedbackDetailView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000';

  useEffect(() => {
    const fetchFeedbackDetail = async () => {
      try {
        const decryptedId = decryptId(id);
        if (!decryptedId) {
          setError('Invalid feedback ID format.');
          setLoading(false);
          return;
        }

        const response = await feedbackService.getFeedbackById(decryptedId);
        if (response && response.status && response.data) {
          setFeedback(response.data);
        } else {
          setError('Feedback not found.');
        }
      } catch (err) {
        console.error('Failed to load feedback details', err);
        setError('Failed to load feedback details.');
      } finally {
        setLoading(false);
      }
    };

    fetchFeedbackDetail();
  }, [id]);

  const getImageUrl = (path) => {
    if (!path) return '';
    return `${API_URL}${path}`;
  };

  const renderStars = (rating) => {
    return '★'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  if (loading) return <div className={styles.loading}>Loading Feedback Details...</div>;
  if (error || !feedback) return <div className={styles.error}>{error}</div>;

  return (
    <div className={styles.container}>
      <div style={{ marginBottom: '1.5rem' }}>
        <button className={styles.backBtn} onClick={() => navigate('/app/user-feedback-list')}>
          <ChevronLeft size={18} />
          Back to Feedback List
        </button>
      </div>

      <div className={styles.contentGrid}>
        
        {/* Main Left Section */}
        <div className={styles.mainSection}>
          
          {/* Detailed Ratings */}
          <div className={styles.card}>
            <div className={styles.cardTitle}>
              <Star size={20} />
              Satisfaction Ratings
            </div>
            <div className={styles.ratingGrid}>
              <div className={styles.ratingItem}>
                <span className={styles.ratingLabel}>Overall Satisfaction</span>
                <span className={styles.stars}>{renderStars(feedback.overallSatisfaction)}</span>
              </div>
              <div className={styles.ratingItem}>
                <span className={styles.ratingLabel}>Facility Cleanliness</span>
                <span className={styles.stars}>{renderStars(feedback.facilityCleanliness)}</span>
              </div>
              <div className={styles.ratingItem}>
                <span className={styles.ratingLabel}>Equipment Quality</span>
                <span className={styles.stars}>{renderStars(feedback.equipmentQuality)}</span>
              </div>
              <div className={styles.ratingItem}>
                <span className={styles.ratingLabel}>Staff Behavior</span>
                <span className={styles.stars}>{renderStars(feedback.staffBehavior)}</span>
              </div>
              <div className={styles.ratingItem}>
                <span className={styles.ratingLabel}>Trainer Feedback</span>
                <span className={styles.stars}>{renderStars(feedback.trainerFeedback)}</span>
              </div>
            </div>
          </div>

          {/* Comments */}
          <div className={styles.card}>
            <div className={styles.cardTitle}>
              <MessageSquare size={20} />
              Written Comments
            </div>
            {feedback.comments ? (
              <div className={styles.commentBox}>{feedback.comments}</div>
            ) : (
              <div style={{ color: '#94a3b8', fontStyle: 'italic' }}>No comments provided.</div>
            )}
          </div>

          {/* Photos */}
          <div className={styles.card}>
            <div className={styles.cardTitle}>
              <Camera size={20} />
              Progress Photos
            </div>
            <div className={styles.photoGrid}>
              <div className={styles.photoColumn}>
                <h4>Before Image</h4>
                <div className={styles.imageContainer}>
                  {feedback.beforeImage ? (
                    <a href={getImageUrl(feedback.beforeImage)} target="_blank" rel="noreferrer">
                      <img src={getImageUrl(feedback.beforeImage)} alt="Before Progress" />
                    </a>
                  ) : (
                    <div className={styles.noImage}>
                      <Camera size={32} opacity={0.5} />
                      No Image Provided
                    </div>
                  )}
                </div>
              </div>
              
              <div className={styles.photoColumn}>
                <h4>After Image</h4>
                <div className={styles.imageContainer}>
                  {feedback.afterImage ? (
                    <a href={getImageUrl(feedback.afterImage)} target="_blank" rel="noreferrer">
                      <img src={getImageUrl(feedback.afterImage)} alt="After Progress" />
                    </a>
                  ) : (
                    <div className={styles.noImage}>
                      <Camera size={32} opacity={0.5} />
                      No Image Provided
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Sidebar Right Section */}
        <div className={styles.sideSection}>
          
          <div className={styles.card}>
            <div className={styles.cardTitle}>
              <User size={20} />
              Member Information
            </div>
            <div className={styles.memberInfo}>
              <div className={styles.infoRow}>
                <span>Name</span>
                <span>{feedback.memberName}</span>
              </div>
              <div className={styles.infoRow}>
                <span>Email</span>
                <span>{feedback.memberEmail}</span>
              </div>
              <div className={styles.infoRow}>
                <span>Phone</span>
                <span>{feedback.phoneNumber || 'N/A'}</span>
              </div>
            </div>
          </div>

          <div className={styles.card}>
            <div className={styles.cardTitle}>
              <Calendar size={20} />
              Submission Details
            </div>
            <div className={styles.memberInfo}>
              <div className={styles.infoRow}>
                <span>Date Submitted</span>
                <span>{new Date(feedback.submittedAt).toLocaleDateString()}</span>
              </div>
              <div className={styles.infoRow}>
                <span>Time Submitted</span>
                <span>{new Date(feedback.submittedAt).toLocaleTimeString()}</span>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default FeedbackDetailView;
